import React, { useCallback, useEffect, useMemo, useState } from "react";
import { commitMutation, graphql } from "react-relay";
import { Link } from "react-router-dom";

import { faAndroid, faApple, faAppStoreIos, faWindows } from "@awesome.me/kit-4c83d50d0d/icons/classic/brands";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { memoize } from "lodash";
import queryString from "query-string";
import { Loader } from "semantic-ui-react";

import Card from "~/account/Integrations/Card";
import type { ActionItemIntegration } from "~/integrations/actionItemIntegration";
import { clickup as clickupIntegration } from "~/integrations/clickup";
import { linear as linearIntegration } from "~/integrations/linear";
import { monday as mondayIntegration } from "~/integrations/monday";
import { trello as trelloIntegration } from "~/integrations/trello";
import { ReactComponent as ConfluenceIcon } from "~/legacy-assets/images/logos/confluence-icon-solid.svg";
import { ReactComponent as FirefoxLogo } from "~/legacy-assets/images/logos/firefox.svg";
import { ReactComponent as ChromeLogo } from "~/legacy-assets/images/logos/google_chrome.svg";
import { ReactComponent as MeetIcon } from "~/legacy-assets/images/logos/google_meet.svg";
import { ReactComponent as GSuiteLogo } from "~/legacy-assets/images/logos/gsuite.svg";
import { ReactComponent as MicrosoftLogo } from "~/legacy-assets/images/logos/microsoft.svg";
import { ReactComponent as TeamsIcon } from "~/legacy-assets/images/logos/ms_teams.svg";
import { ReactComponent as NotionLogo } from "~/legacy-assets/images/logos/notion-logo.svg";
import { ReactComponent as SlackLogo } from "~/legacy-assets/images/logos/slack_icon_only.svg";
import { ReactComponent as ZoomLogo } from "~/legacy-assets/images/logos/zoom_icon_only.svg";
import growthbook from "~/lib/ab/growthbook";
import {
	type AuthPostMessage,
	type OAuthParams,
	promptConfluenceAuth,
	promptGoogleAuth,
	promptNotionAuth,
	promptOffice365Auth,
	promptSlackAuth,
} from "~/lib/auth/index";
import { isExtensionInstalled } from "~/lib/browserExtension";
import { warning } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import {
	appStoreLink,
	chromeStoreLink,
	desktopDownloadLink,
	isAndroid,
	isChrome,
	isDesktopApp,
	isFirefox,
	isiOS,
	isMacos,
	isNative,
	isTeams,
	isWindows,
	isZoom,
	mozillaStoreLink,
	msTeamsStoreLink,
	playStoreLink,
	withQueryParams,
} from "~/lib/utils";
import { environment } from "~/models";
import { Divider, SettingsPageSubtitle, SettingsPageTitle } from "~/settings/SettingsModal/shared";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import { showFeatureModal } from "../billing";
import type { FeatureValues } from "../billing/utils";
import { AsanaCard } from "./Integrations/Asana/AsanaCard";
import { ZOOM_APP_UPDATE_FEATURE_FLAG } from "./Integrations/featureFlag";
import { HubspotUserCard } from "./Integrations/Hubspot/HubspotCard";
import { MicrosoftTodoCard } from "./Integrations/MicrosoftTodo/MicrosoftTodoCard";
import { SalesforceCard } from "./Integrations/Salesforce/SalesforceCard";
import { ZapierCard } from "./Integrations/Zapier/ZapierCard";

import type { UserIntegrationsDisconnectIntegrationMutation as DisconnectType } from "~/__generated__/UserIntegrationsDisconnectIntegrationMutation.graphql";
import type { UserIntegrationsQuery as QueryType } from "~/__generated__/UserIntegrationsQuery.graphql";

import styles from "./css/UserIntegrations.module.css";

const isDesktopAppCandidate = !isNative && !isDesktopApp && (isWindows || isMacos);

export const DisconnectIntegrationMutation = graphql`
	mutation UserIntegrationsDisconnectIntegrationMutation($input: DisconnectIntegrationInput!) {
		disconnectIntegration(input: $input) {
			ok
			error
			deletedId
		}
	}
`;

const UserIntegrationsQuery = graphql`
	query UserIntegrationsQuery {
		viewer {
			user {
				internalAttributes {
					isAccountAdmin
				}
			}
			integrations {
				slack {
					id
					isConnected
					name
				}
				google {
					id
					externalId
					name
					isActive
					isGoogleAdmin
					accountManaged
				}
				office365 {
					id
					isAdmin
					externalId
				}
				zapier {
					id
					isConnected
				}
				asana {
					id
					isConnected
					email
				}
				salesforce {
					id
					isConnected
				}
				hubspot {
					id
					isConnected
				}
				zoom {
					id
					isConnected
					name
					hasLocalRecordingTokenScope
				}
				clickup {
					id
					isConnected
				}
				trello {
					id
					isConnected
				}
				monday {
					id
					isConnected
				}
				mondayOauthClientId
				linear {
					id
					isConnected
				}
				microsoftTodo {
					id
					isConnected
				}
				notion {
					id
					isConnected
					siteName
				}
				confluence {
					id
					isConnected
					siteName
					requiresReconnect
				}
			}
			account {
				billingAccount {
					hasAccessToZapierIntegration: hasAccessToFeature(feature: zapier_integration)
					featureAccess
				}
				primaryAdmin {
					fullName
				}
				integrations {
					slack {
						id
						domain
						externalId
						name
						botId
						botName
					}
					gsuite {
						id
						externalId
					}
					office365 {
						id
						externalId
					}
					hubspot {
						accountConnected
						userConnected
					}
				}
			}
		}
	}
`;

export default withSuspense(<Loader active />, UserIntegrations);

export type IntegrationSubpageKey = "zapier" | "asana" | "microsoft-todo" | "salesforce" | "hubspot";

interface IntegrationCardProps {
	name: string;
	description: string;
	logo: JSX.Element | React.ReactNode;
	connection: {
		id: string;
		externalId?: string;
		name?: string;
		accountManaged?: boolean;
		isConnected?: boolean;
		siteName?: string;
	} | null;
	prompt?: ((params: OAuthParams) => Promise<AuthPostMessage>) | null;
	permissionsDenied?: string;
	connectionLink?: string | null;
	disconnectWarning?: React.ReactElement;
	retry: () => void;
	selected: boolean;
	hideActions?: boolean;
	allowMulti?: boolean;
	extraConnectOptions?: { name: string; action: () => void }[];
	showUpdate?: boolean;
}

function Integration({
	name,
	description,
	logo,
	connection,
	prompt,
	permissionsDenied = "",
	connectionLink,
	disconnectWarning,
	retry,
	selected,
	hideActions = false,
	allowMulti = false,
	extraConnectOptions,
	showUpdate,
}: IntegrationCardProps) {
	const isConnected = connection != null && Boolean(connection.externalId || connection.isConnected);

	const onConnectClicked = useCallback(() => {
		if (prompt != null) {
			prompt({ refresh: true })
				.then(() => {
					retry();
					return toast.success(`Connected ${name}`);
				})
				.catch(function (exc) {
					if (exc.code === permissionsDenied) {
						return toast.error(`Fellow requires your calendar (${exc.message})`);
					} else {
						Sentry.captureException(exc);
						return toast.error(exc.message);
					}
				});
		}

		if (connectionLink != null) window.open(connectionLink, "_blank", "noopener noreferrer");
	}, [connectionLink, name, permissionsDenied, prompt, retry]);

	const onDisconnectClicked = useCallback(async () => {
		if (
			!connection?.id ||
			!(await warning(
				disconnectWarning ? (
					disconnectWarning
				) : (
					<div>
						Are you sure you want to disconnect {name}? <br />
						Any data tied to it may be permanently lost.
					</div>
				),
				{ title: `Disconnect ${name}`, yesProps: { variant: "destructive" } },
			))
		) {
			return;
		}

		commitMutation<DisconnectType>(environment, {
			mutation: DisconnectIntegrationMutation,
			variables: {
				input: {
					id: connection.id,
				},
			},
			configs: [
				{
					type: "NODE_DELETE",
					deletedIDFieldName: "deletedId",
				},
			],
			onCompleted: ({ disconnectIntegration }) => {
				if (disconnectIntegration && !disconnectIntegration.error) {
					toast.success(`Disconnected ${name}`);
				} else if (disconnectIntegration) {
					toast.error(disconnectIntegration.error);
				}
			},
		});
	}, [disconnectWarning, name, connection]);

	const onUpdateClicked = useCallback(() => {
		window.open(`${window.location.origin}${connectionLink}`, "_blank", "noopener");
	}, [connectionLink]);

	const disconnectOption = (
		<Menu.Item key="disconnect" onSelect={onDisconnectClicked}>
			Disconnect
		</Menu.Item>
	);

	let connectOptions = isConnected
		? showUpdate
			? [
					<Menu.Item key="update" onSelect={onUpdateClicked}>
						Update
					</Menu.Item>,
					disconnectOption,
				]
			: [disconnectOption]
		: [
				<Menu.Item key="connect" onSelect={onConnectClicked}>
					Connect
				</Menu.Item>,
			];

	if (isConnected && allowMulti && growthbook.isOn("slack-multi-workspace")) {
		connectOptions.push(
			<Menu.Item key="connect" onSelect={onConnectClicked}>
				Connect another
			</Menu.Item>,
		);
	}

	if (extraConnectOptions)
		connectOptions = connectOptions.concat(
			extraConnectOptions.map(option => {
				return (
					<Menu.Item key={option.name} onSelect={option.action}>
						{option.name}
					</Menu.Item>
				);
			}),
		);

	return (
		<Card
			name={name}
			description={description}
			connected={isConnected}
			actions={hideActions ? [] : connectOptions}
			logo={logo}
			className={cx(styles.extensionCard, { [styles.selected]: selected })}
		>
			<div className={styles.cardContent}>
				{connection && connection.name && isConnected && (
					<span>
						Connected as <a>{connection.name}</a>
					</span>
				)}
				{connection && connection.accountManaged && <span>This integration is managed by your account admin.</span>}
				{connection && connection.siteName && (
					<span>
						Connected to <strong>{connection.siteName}</strong>
					</span>
				)}
			</div>
		</Card>
	);
}

function MaybePaywalled({
	children,
	feature,
	billingAccount,
}: {
	children: JSX.Element;
	feature: FeatureValues;
	billingAccount: QueryType["response"]["viewer"]["account"]["billingAccount"];
}) {
	const onClick = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			showFeatureModal(feature);
		},
		[feature],
	);
	if (billingAccount.featureAccess[feature]) {
		return children;
	}
	return (
		<div onClickCapture={onClick} style={{ cursor: "pointer" }}>
			{children}
		</div>
	);
}

function UserIntegrations(props: {
	highlightedExtension?: string;
	onSelectSubpage?(key: IntegrationSubpageKey): void;
}) {
	const [fetchKey, setFetchKey] = React.useState(0);
	const { viewer } = useLazyLoadQuery<QueryType>(
		UserIntegrationsQuery,
		{},
		{ fetchPolicy: "store-and-network", fetchKey },
	);

	const retry = React.useCallback(() => void setFetchKey(n => n + 1), [setFetchKey]);

	const queryObj = queryString.parse(window.location.search);
	// NOTE: queryObj.itag can be undefined, but its types say otherwise
	const appExtension = queryObj.itag || null;
	const highlightedExtension = appExtension
		? typeof appExtension === "string"
			? appExtension
			: appExtension[appExtension.length - 1]
		: props.highlightedExtension;

	const { onSelectSubpage } = props;
	const getSubpageClickHandler = useMemo(
		() =>
			memoize((key: IntegrationSubpageKey) => (e: React.MouseEvent<HTMLAnchorElement>) => {
				if (["Meta", "Control", "Alt", "Shift"].some(key => e.getModifierState(key)) || e.defaultPrevented) return;
				if (!onSelectSubpage) return; // onSelectSubpage is still optional so the old non-modal page still works
				e.preventDefault();
				onSelectSubpage(key);
			}),
		[onSelectSubpage],
	);

	const [installedApps, setInstalledApps] = useState<string[]>([]);

	useEffect(() => {
		navigator.getInstalledRelatedApps?.().then(function (apps) {
			setInstalledApps(apps?.map(({ platform }) => platform));
		});
	}, []);

	type IntegrationsListProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

	function IntegrationsList({ children, ...props }: IntegrationsListProps) {
		const scrollRef = React.useRef<HTMLDivElement | null>(null);

		React.useEffect(() => {
			if (highlightedExtension === undefined) return;

			let cards: Array<string> = [];
			React.Children.forEach(children, child => {
				if (!React.isValidElement(child) || child.key === null) return;
				cards.push(child.key.toString());
			});

			if (cards.includes(highlightedExtension)) scrollRef.current?.scrollIntoView({ behavior: "smooth" });
		}, [children]);

		return (
			<div ref={scrollRef} {...props}>
				{children}
			</div>
		);
	}

	function ActionItemIntegrationUserSettingsCard({
		integration,
		connection,
		extraConnectOptions,
	}: {
		integration: ActionItemIntegration;
		connection: {
			readonly id: string;
			readonly isConnected: boolean;
		} | null;
		extraConnectOptions?: { name: string; action: () => void }[];
	}) {
		const { displayName, externalActionItemName, logo, promptAuth, key } = integration;

		const description = `Create, import, and manage ${displayName} ${externalActionItemName}s from Fellow.`;

		let integrationProps: IntegrationCardProps = {
			name: displayName,
			description: description,
			disconnectWarning: (
				<div>
					Are you sure you want to disconnect {displayName}? <br />
					<p className={styles.helpText}>
						<b>Note: </b>Previously connected {externalActionItemName}s will still exist in {displayName}. Disconnecting
						{` ${displayName}`} will simply disable syncing.
					</p>
				</div>
			),
			logo: <div className={styles.settingsIntegrationLogo}>{logo}</div>,
			connection: connection,
			retry: retry,
			selected: isCardSelected(key.toString().toLowerCase()),
			extraConnectOptions: extraConnectOptions,
		};
		if (promptAuth) integrationProps.prompt = promptAuth;

		return <Integration {...integrationProps} />;
	}

	function isCardSelected(key: string): boolean {
		if (!appExtension || !key) return false;
		if (typeof appExtension === "string") return appExtension === key;
		return appExtension.includes(key);
	}

	const {
		slack,
		google,
		office365,
		zapier,
		salesforce,
		hubspot,
		asana,
		zoom,
		clickup,
		trello,
		monday,
		mondayOauthClientId,
		linear,
		microsoftTodo,
		notion,
		confluence,
	} = viewer.integrations;

	const openMondayInstallWindow = useCallback(() => {
		window.open(
			`https://auth.monday.com/oauth2/authorize?client_id=${mondayOauthClientId}&response_type=install`,
			"_blank",
			"noopener noreferrer",
		);
	}, [mondayOauthClientId]);
	const zoomAppUpdateFlagIsOn = useFeatureIsOn(ZOOM_APP_UPDATE_FEATURE_FLAG);

	const isAnyCalendarConnected = Boolean(google?.externalId) || Boolean(office365?.externalId);

	const calendarIntegrations = (
		<>
			<div className={styles.integrationH6}>Calendar Sync</div>
			<IntegrationsList className={styles.integrationsList}>
				<Integration
					name="Google Calendar"
					description="Sync your Google Workspace calendar with Fellow."
					logo={<GSuiteLogo style={{ width: "auto", height: "24px" }} />}
					connection={google}
					prompt={promptGoogleAuth}
					permissionsDenied="access_denied"
					retry={retry}
					selected={isCardSelected("google")}
				/>
				<Integration
					name="Microsoft 365 Calendar"
					description="Sync your Microsoft 365 calendar with Fellow"
					logo={<MicrosoftLogo style={{ width: "auto", height: "24px" }} />}
					connection={office365}
					prompt={promptOffice365Auth}
					permissionsDenied="consent_required"
					retry={retry}
					selected={isCardSelected("office365")}
				/>
			</IntegrationsList>
		</>
	);

	return (
		<>
			<SettingsPageTitle>Apps, extensions &amp; integrations</SettingsPageTitle>
			<SettingsPageSubtitle>
				Download our native apps and connect our integrations to truly master Fellow
			</SettingsPageSubtitle>
			<Divider />

			{!isAnyCalendarConnected && calendarIntegrations}
			<div className={styles.integrationH6}>Native Apps</div>
			<IntegrationsList className={styles.integrationsList}>
				{isDesktopAppCandidate && (
					<a
						key="desktop"
						// eslint-disable-next-line camelcase
						href={withQueryParams(desktopDownloadLink, { utm_content: "user_integrations" })}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Card
							recommended
							name={isMacos ? "macOS" : "Windows"}
							description="Take everything you love about Fellow and imagine it in a lightweight desktop app."
							logo={<FontAwesomeIcon icon={isMacos ? faApple : faWindows} className={styles.extensionIcon} />}
							connected={isDesktopApp}
							disconnectedLabel="Download"
							className={cx(styles.extensionCard, {
								[styles.windows]: isWindows,
								[styles.macos]: isMacos,
								[styles.selected]: isCardSelected("desktop"),
							})}
						/>
					</a>
				)}

				{!isNative && !isAndroid && (
					<a key="ios" href={appStoreLink} target="_blank" rel="noopener noreferrer">
						<Card
							key="ios"
							name="iOS"
							description={
								"Work needs to meet you where you are, so we’ve built an iOS app that is lightweight and feature-rich."
							}
							logo={<FontAwesomeIcon icon={faAppStoreIos} className={styles.extensionIcon} />}
							connected={installedApps.includes("itunes")}
							connectedLabel="Installed"
							disconnectedLabel="Get it now"
							className={cx(styles.extensionCard, styles.ios, {
								[styles.selected]: isCardSelected("ios"),
							})}
						/>
					</a>
				)}

				{!isNative && !isiOS && (
					<a key="android" href={playStoreLink} target="_blank" rel="noopener noreferrer">
						<Card
							key="android"
							name="Android"
							description={
								"Fellow’s Android app will ensure that you’re always prepared to have productive conversations."
							}
							logo={<FontAwesomeIcon icon={faAndroid} className={styles.extensionIcon} />}
							connected={installedApps.includes("play")}
							connectedLabel="Installed"
							disconnectedLabel="Get it now"
							className={cx(styles.extensionCard, styles.android, {
								[styles.selected]: isCardSelected("android"),
							})}
						/>
					</a>
				)}
			</IntegrationsList>

			<div className={styles.integrationH6}>Video Conferencing</div>
			<IntegrationsList className={styles.integrationsList}>
				<Integration
					name="Zoom"
					description="Edit your meeting notes within Zoom."
					logo={<ZoomLogo style={{ width: "auto", height: "24px" }} />}
					connection={zoom}
					connectionLink="/zoom/oauth/"
					showUpdate={Boolean(zoom && !zoom.hasLocalRecordingTokenScope && zoomAppUpdateFlagIsOn)}
					hideActions={isZoom}
					selected={isCardSelected("zoom")}
					retry={retry}
				/>
				{(isChrome || (!isFirefox && !isChrome)) && (
					<a
						key="chrome"
						/* eslint-disable-next-line camelcase */
						href={withQueryParams(chromeStoreLink, { utm_content: "user_integrations" })}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Card
							key="meet"
							name="Google Meet for Chrome"
							description="For those who spend too much time balancing browser tabs during video calls."
							logo={
								<div className={styles.extensionIcons}>
									<MeetIcon className={styles.extensionIcon} />
									<ChromeLogo className={styles.extensionIcon} />
								</div>
							}
							connected={isExtensionInstalled()}
							connectedLabel="Installed"
							disconnectedLabel="Add to Chrome"
							className={cx(styles.extensionCard, {
								[styles.selected]: isCardSelected("chrome"),
							})}
						/>
					</a>
				)}
				{(isFirefox || (!isFirefox && !isChrome)) && (
					<a
						key="firefox"
						/* eslint-disable-next-line camelcase */
						href={withQueryParams(mozillaStoreLink, { utm_content: "user_integrations" })}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Card
							key="meet"
							name="Google Meet for Firefox"
							description="For those who spend too much time balancing browser tabs during video calls."
							logo={
								<div className={styles.extensionIcons}>
									<MeetIcon className={styles.extensionIcon} />
									<FirefoxLogo className={styles.extensionIcon} />
								</div>
							}
							connected={isExtensionInstalled()}
							disconnectedLabel="Add to Firefox"
							className={cx(styles.extensionCard, {
								[styles.selected]: isCardSelected("firefox"),
							})}
						/>
					</a>
				)}
				<a
					key="teams"
					/* eslint-disable-next-line camelcase */
					href={withQueryParams(msTeamsStoreLink, { utm_content: "user_integrations" })}
					target="_blank"
					rel="noopener noreferrer"
				>
					<Card
						key="teams"
						name="Microsoft Teams"
						description="Keep Fellow close at hand with our Microsoft Teams app."
						logo={<TeamsIcon className={styles.extensionIcon} />}
						connected={isTeams}
						disconnectedLabel="Get it now"
						className={cx(styles.extensionCard, {
							[styles.selected]: isCardSelected("teams"),
						})}
					/>
				</a>
			</IntegrationsList>

			<div className={styles.integrationH6}>Automation</div>
			<IntegrationsList className={styles.integrationsList}>
				<Integration
					name="Slack"
					description="Receive notifications and use Fellow right from Slack."
					logo={<SlackLogo style={{ width: "auto", height: "24px" }} />}
					connection={slack ? slack[0] : null}
					prompt={promptSlackAuth}
					retry={retry}
					selected={isCardSelected("slack")}
					allowMulti
				/>
				<MaybePaywalled feature="zapier_integration" billingAccount={viewer.account.billingAccount}>
					<Link onClick={getSubpageClickHandler("zapier")} to="/settings/integrations/zapier/">
						<ZapierCard
							key="zapier"
							connected={zapier?.isConnected ?? false}
							className={cx(styles.extensionCard, {
								[styles.selected]: isCardSelected("zapier"),
							})}
						/>
					</Link>
				</MaybePaywalled>
				<MaybePaywalled feature="clickup_integration" billingAccount={viewer.account.billingAccount}>
					<ActionItemIntegrationUserSettingsCard integration={clickupIntegration} connection={clickup} />
				</MaybePaywalled>
				<MaybePaywalled feature="monday_integration" billingAccount={viewer.account.billingAccount}>
					<ActionItemIntegrationUserSettingsCard
						integration={mondayIntegration}
						connection={monday}
						extraConnectOptions={
							!monday?.isConnected
								? [
										{
											name: "Install",
											action: openMondayInstallWindow,
										},
									]
								: undefined
						}
					/>
				</MaybePaywalled>
				<MaybePaywalled feature="trello_integration" billingAccount={viewer.account.billingAccount}>
					<ActionItemIntegrationUserSettingsCard integration={trelloIntegration} connection={trello} />
				</MaybePaywalled>
				<Link onClick={getSubpageClickHandler("asana")} to="/settings/integrations/asana/">
					<AsanaCard
						key="asana"
						connection={asana}
						className={cx(styles.extensionCard, {
							[styles.selected]: isCardSelected("asana"),
						})}
					/>
				</Link>
				<MaybePaywalled feature="linear_integration" billingAccount={viewer.account.billingAccount}>
					<ActionItemIntegrationUserSettingsCard integration={linearIntegration} connection={linear} />
				</MaybePaywalled>
				{office365 && (
					<MaybePaywalled feature="microsoft_todo_integration" billingAccount={viewer.account.billingAccount}>
						<Link onClick={getSubpageClickHandler("microsoft-todo")} to="/settings/integrations/microsoft-todo/">
							<MicrosoftTodoCard
								key="microsoftTodo"
								connection={microsoftTodo}
								className={cx(styles.extensionCard, {
									[styles.selected]: isCardSelected("microsoftTodo"),
								})}
							/>
						</Link>
					</MaybePaywalled>
				)}
			</IntegrationsList>

			<div className={styles.integrationH6}>Knowledge Management Systems</div>
			<IntegrationsList className={styles.integrationsList}>
				<Integration
					name="Notion"
					description="Export notes from Fellow directly to your Notion workspace."
					logo={<NotionLogo style={{ width: "24px", height: "24px" }} />}
					connection={notion}
					prompt={promptNotionAuth}
					permissionsDenied="access_denied"
					retry={retry}
					selected={isCardSelected("notion")}
					disconnectWarning={
						<div>
							Are you sure you want to disconnect Notion? <br />
							<p className={styles.helpText}>
								<b>Note: </b>Previously exported notes will still exist in Notion.
							</p>
						</div>
					}
				/>
				<Integration
					name="Confluence"
					description="Export notes from Fellow directly to your Confluence spaces."
					logo={<ConfluenceIcon style={{ width: "24px", height: "24px" }} />}
					connection={confluence}
					prompt={promptConfluenceAuth}
					showUpdate={Boolean(confluence && confluence.requiresReconnect)}
					disconnectWarning={
						<div>
							Are you sure you want to disconnect Confluence? <br />
							<p className={styles.helpText}>
								<b>Note: </b>Previously exported notes will still exist in Confluence.
							</p>
						</div>
					}
					permissionsDenied="access_denied"
					retry={retry}
					selected={isCardSelected("confluence")}
				/>
			</IntegrationsList>

			<div className={styles.integrationH6}>CRM</div>
			<IntegrationsList className={styles.integrationsList}>
				<Link onClick={getSubpageClickHandler("salesforce")} to="/settings/integrations/salesforce/">
					<SalesforceCard
						key="salesforce"
						connection={salesforce}
						className={cx(styles.extensionCard, {
							[styles.selected]: isCardSelected("salesforce"),
						})}
					/>
				</Link>

				<Link onClick={getSubpageClickHandler("hubspot")} to="/settings/integrations/hubspot/">
					<HubspotUserCard
						key="hubspot"
						connection={hubspot}
						viewer={viewer}
						className={cx(styles.extensionCard, {
							[styles.selected]: isCardSelected("hubspot"),
						})}
					/>
				</Link>
			</IntegrationsList>
			{isAnyCalendarConnected && calendarIntegrations}
		</>
	);
}
